import { getDisplayName } from '@/lib/crm/utils/getDisplayName';
import { type CrmObject, getPropertyValue } from '@/types/crm';

/**
 * `Number(raw)` on a malformed value (e.g. an empty or non-numeric property
 * string) produces `NaN`, which the old `!= null` guards did not catch
 * (`NaN == null` is `false`) — this centralizes the finite check so a bad
 * `ahoy_uploaded_file_id` can't slip through as a "valid" id.
 */
export function toFiniteFileId(
  raw: string | number | undefined
): number | null {
  if (raw == null || raw === '') {
    return null;
  }
  const parsed = Number(raw);
  return Number.isFinite(parsed) ? parsed : null;
}

/**
 * Extracts the filename and uploaded-file id from a file entity's CRM
 * properties. Shared by the row renderer and the download-all path so both
 * apply the same `ahoy_filename` fallback and finite-id check.
 */
export function getAttachmentFileMeta(obj: CrmObject | undefined): {
  filename: string;
  uploadedFileId: number | null;
} {
  const filename =
    getPropertyValue<string>(obj?.properties?.ahoy_filename) ??
    (obj ? getDisplayName(obj, 'File') : 'File');
  const rawUploadedId = getPropertyValue<string | number>(
    obj?.properties?.ahoy_uploaded_file_id
  );
  return { filename, uploadedFileId: toFiniteFileId(rawUploadedId) };
}
